import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { storedToken } from "@common/api";
import MaterialIcon from "@common/components/MaterialIcon";
import { formatBytes } from "@common/format";
import { useAppSelector } from "@common/store/hooks";
import { playKey } from "./playtime";
import { libraryKeys, useCloudRoms } from "./queries";
import { uploadToLibrary } from "./cloudLibrary";
import { desktop, isDesktop } from "../desktop/desktop";
import { ejsSystemOf, isEjsSystem, rememberSystem, zipEntryNames } from "../emulator/ejs";
import { displayName, fileExtension, romBaseName } from "../emulator/saves";
import { listLocalGames, SCANNED, storeGames, SYSTEM_LABEL, systemOf, type System } from "./localLibrary";

/** One file a scan turned up, as the choice shows it. */
interface Candidate {
  key: string;
  name: string;
  size: number;
  /** What it seems to be, from its name or (for a zip) its contents. */
  what: string;
  /** Its console, when the name or zip says; a disc .iso is settled once added. */
  system: System | null;
  /** Already in the library here. */
  present: boolean;
  /** The same game is in the cloud library under this name: this file becomes its local copy. */
  cloudName?: string;
  cloudSystem?: string;
  file?: File;
  path?: string;
}

const CARTRIDGE = /\.(gb|gbc|gba|n64|z64|v64)$/i;

/** What a browser-picked file is: its console, from the name or a zip's contents. */
async function describe(file: File): Promise<{ what: string; system: System | null }> {
  if (CARTRIDGE.test(file.name)) return { what: SYSTEM_LABEL[systemOf(file.name)], system: systemOf(file.name) };
  const ejs = await ejsSystemOf(file.name, file).catch(() => null);
  if (ejs) return { what: SYSTEM_LABEL[ejs as System], system: ejs as System };
  const inside = await zipEntryNames(file).catch(() => [] as string[]);
  const cartridge = inside.find((entry) => CARTRIDGE.test(entry));
  return cartridge
    ? { what: `${SYSTEM_LABEL[systemOf(cartridge)]} (zip)`, system: systemOf(cartridge) }
    : { what: "Zip of something else", system: null };
}

/** The console a desktop scan's description names, if it names one. */
function systemNamed(what: string): System | null {
  const found = (Object.entries(SYSTEM_LABEL) as [System, string][]).find(([, label]) => what.startsWith(label));
  return found ? found[0] : null;
}

// Reading a folder in a browser needs its consent. Chrome's directory
// picker asks with a small "view files" bar it remembers for the session
// and hands over handles, so only the game-looking files are read; it does
// refuse a few folders (Downloads, Desktop, the home folder) as "system"
// ones, and then the plain folder input is used, which Chrome gates with a
// dialog about "uploading" every file. Safari and Firefox only have the
// input.
interface DirectoryHandle {
  values(): AsyncIterable<{ kind: "file" | "directory"; name: string; getFile?: () => Promise<File> } & Partial<DirectoryHandle>>;
}
const pickDirectory = (): Promise<DirectoryHandle> | null =>
  "showDirectoryPicker" in window ? (window as unknown as { showDirectoryPicker(): Promise<DirectoryHandle> }).showDirectoryPicker() : null;

/** The game-looking files under a picked folder, a few levels deep, hidden folders skipped. */
async function filesUnder(folder: DirectoryHandle, depth = 0): Promise<File[]> {
  const files: File[] = [];
  for await (const entry of folder.values()) {
    if (entry.name.startsWith(".")) continue;
    if (entry.kind === "directory") {
      if (depth < 6 && entry.values) files.push(...(await filesUnder(entry as DirectoryHandle, depth + 1)));
    } else if (SCANNED.test(entry.name) && entry.getFile) {
      files.push(await entry.getFile());
    }
    if (files.length >= 5000) break;
  }
  return files;
}

/**
 * Adds the games in a folder, after the player picks which and where: each
 * one here, and (signed in) to the shared cloud library too. In the browser
 * the folder is read as above; in the desktop app Rust walks it, and chosen
 * disc games are linked into the games folder. Calls `onDone` with a line
 * to show once it's finished.
 */
export default function ScanFolderButton({
  className,
  iconOnly = false,
  onDone,
}: {
  className: string;
  /** Just the folder icon, with the words as its tip. */
  iconOnly?: boolean;
  onDone: (note: string) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [scanning, setScanning] = useState(false);
  const [found, setFound] = useState<Candidate[] | null>(null);
  const [skipped, setSkipped] = useState(0);
  /** Keys of the games to add here. */
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  /** Keys of the games to upload to the cloud library as well. */
  const [uploads, setUploads] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<string | null>(null);
  const { isAuthenticated } = useAppSelector((s) => s.auth);
  const cloud = useCloudRoms(isAuthenticated).data ?? [];
  const queryClient = useQueryClient();

  // Games already here aren't offered again (the same game under another
  // file name counts too). One that's only in the cloud library is: the
  // file becomes its local copy, named so its saves line up.
  const have = async () => {
    const local = await listLocalGames().catch(() => []);
    return {
      local: new Set(local.map((g) => playKey(g.name))),
      cloud: new Map(cloud.map((r) => [playKey(r.name), r])),
    };
  };

  const offer = (candidates: Candidate[]) => {
    const fresh = candidates.filter((c) => !c.present);
    if (!fresh.length) {
      onDone(candidates.length ? "Every game there is already in your library." : "No games found there.");
      return;
    }
    setSkipped(candidates.length - fresh.length);
    setFound(fresh);
    setChosen(new Set(fresh.map((c) => c.key)));
    setUploads(new Set());
  };

  // The browser: the folder's files, described one by one.
  const scanFiles = async (files: File[]) => {
    setScanning(true);
    try {
      const known = await have();
      const games = files.filter((file) => SCANNED.test(file.name) && file.size <= 128 * 1024 * 1024);
      offer(
        await Promise.all(
          games.map(async (file) => {
            const inCloud = known.cloud.get(playKey(file.name));
            const { what, system } = await describe(file);
            return {
              key: file.name,
              name: file.name,
              size: file.size,
              what,
              system,
              present: known.local.has(playKey(file.name)),
              cloudName: inCloud?.name,
              cloudSystem: inCloud?.system,
              file,
            };
          }),
        ),
      );
    } catch (e) {
      onDone(e instanceof Error ? e.message : String(e));
    } finally {
      setScanning(false);
    }
  };

  // The app: Rust lists the folder.
  const scanApp = async () => {
    setScanning(true);
    try {
      const known = await have();
      offer(
        (await desktop.scanFolder()).map((f) => ({
          key: f.path,
          name: f.name,
          size: f.size,
          what: f.what,
          system: systemNamed(f.what),
          present: f.present || known.local.has(playKey(f.name)),
          cloudName: known.cloud.get(playKey(f.name))?.name,
          path: f.path,
        })),
      );
    } catch (e) {
      onDone(e instanceof Error ? e.message : String(e));
    } finally {
      setScanning(false);
    }
  };

  /** Uploads a game just added here to the cloud library; "" on success, else why not. */
  const upload = async (c: Candidate, file: File | null): Promise<string> => {
    setProgress(`Uploading ${displayName(c.name)}…`);
    const token = storedToken() ?? "";
    try {
      if (file) {
        const result = await uploadToLibrary(file, c.system ?? undefined);
        return result === "too-large" ? `${displayName(c.name)} is too big for the cloud library (32 MB).` : "";
      }
      // A disc in the app: the games folder has it now, and knows its console.
      const added = (await listLocalGames()).find((g) => g.name === c.name);
      if (!added) return `${displayName(c.name)} wasn't added, so it wasn't uploaded.`;
      await desktop.uploadGame(c.name, added.system, token, ({ phase, fraction }) =>
        setProgress(`${phase === "hashing" ? "Checking" : "Uploading"} ${displayName(c.name)} ${Math.round(fraction * 100)}%`),
      );
      return "";
    } catch (e) {
      return `${displayName(c.name)}: ${e instanceof Error ? e.message : String(e)}`;
    }
  };

  const add = async () => {
    if (!found) return;
    const picked = found.filter((c) => chosen.has(c.key));
    setProgress("Adding…");
    try {
      // A file for a game that's in the cloud takes the library's name (with
      // its own extension) so the cloud saves pair with it.
      const asLibraryCopy = (file: File) => {
        const c = picked.find((p) => p.name === file.name);
        if (!c?.cloudName) return file;
        const named = `${romBaseName(c.cloudName)}.${fileExtension(file.name)}`;
        if (c.cloudSystem && isEjsSystem(c.cloudSystem)) rememberSystem(named, c.cloudSystem);
        return new File([file], named, { type: file.type });
      };
      let count = 0;
      const webFiles = new Map<string, File>();
      if (isDesktop) {
        const { files, added } = await desktop.addFound(picked.map((c) => c.path!));
        for (const file of files) webFiles.set(file.name, file);
        count = added + (await storeGames(files.map(asLibraryCopy))).length;
      } else {
        for (const c of picked) webFiles.set(c.name, c.file!);
        count = (await storeGames(picked.map((c) => asLibraryCopy(c.file!)))).length;
      }
      const problems: string[] = [];
      let uploaded = 0;
      for (const c of picked.filter((p) => uploads.has(p.key))) {
        const problem = await upload(c, webFiles.get(c.name) ?? null);
        if (problem) problems.push(problem);
        else uploaded += 1;
      }
      if (uploaded) libraryKeys.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
      const parts = [
        count ? `Added ${count} game${count === 1 ? "" : "s"}.` : "Nothing new was added.",
        uploaded ? `Uploaded ${uploaded} to the cloud library.` : "",
        ...problems,
      ];
      onDone(parts.filter(Boolean).join(" "));
      setFound(null);
    } catch (e) {
      onDone(e instanceof Error ? e.message : String(e));
    } finally {
      setProgress(null);
    }
  };

  const toggleIn = (set: Set<string>, key: string) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  };
  const allChosen = !!found?.length && found.every((c) => chosen.has(c.key));
  // Uploadable: chosen, not in the cloud already, and known to the library's rules.
  const uploadable = (c: Candidate) => chosen.has(c.key) && !c.cloudName && c.what !== "Zip of something else";
  const canUpload = isAuthenticated;
  const allUploads = !!found?.length && found.filter(uploadable).length > 0 && found.filter(uploadable).every((c) => uploads.has(c.key));

  const label = scanning ? "Scanning…" : "Scan folder…";
  return (
    <>
      <button
        type="button"
        className={className}
        disabled={scanning}
        aria-label={iconOnly ? label : undefined}
        data-tip={iconOnly ? label : undefined}
        onClick={() => {
          if (isDesktop) return scanApp();
          const picked = pickDirectory();
          if (!picked) return input.current?.click();
          picked.then(
            (folder) => filesUnder(folder).then(scanFiles),
            (e: unknown) => {
              // Cancelled is fine; a refused folder gets the plain input.
              if ((e as { name?: string } | null)?.name !== "AbortError") input.current?.click();
            },
          );
        }}
      >
        {scanning ? <span className="spinner" aria-hidden="true" /> : <MaterialIcon name="folder_open" />}
        {!iconOnly && label}
      </button>
      <input
        ref={input}
        type="file"
        hidden
        multiple
        {...{ webkitdirectory: "" }}
        onChange={(e) => {
          const files = [...(e.target.files ?? [])];
          e.target.value = "";
          if (files.length) scanFiles(files);
        }}
      />
      {found && (
        <div className="modal-overlay" onClick={() => !progress && setFound(null)}>
          <div className="modal scan-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Games found</h2>
              <button type="button" className="btn icon-btn" disabled={!!progress} onClick={() => setFound(null)} aria-label="Close">
                <MaterialIcon name="close" />
              </button>
            </div>
            <div className="modal-body">
              <p className="muted small scan-note">
                {found.length} {found.length === 1 ? "game" : "games"}
                {skipped > 0 && ` · ${skipped} already in your library, left out`}.
                {canUpload ? " Tick what to add here, and what to put in the shared cloud library as well." : " Sign in to put games in the shared cloud library too."}
              </p>
              <div className={`scan-grid${canUpload ? " with-cloud" : ""}`}>
                <div className="scan-head">
                  <span />
                  <label className="scan-col">
                    <input type="checkbox" checked={allChosen} onChange={() => setChosen(allChosen ? new Set() : new Set(found.map((c) => c.key)))} />
                    {isDesktop ? "This Mac" : "This browser"}
                  </label>
                  {canUpload && (
                    <label className="scan-col">
                      <input
                        type="checkbox"
                        checked={allUploads}
                        disabled={!found.some(uploadable)}
                        onChange={() => setUploads(allUploads ? new Set() : new Set(found.filter(uploadable).map((c) => c.key)))}
                      />
                      Cloud library
                    </label>
                  )}
                </div>
                {found.map((c) => (
                  <div key={c.key} className="scan-row">
                    <span className="scan-text">
                      <strong>{displayName(c.name)}</strong>
                      <span className="muted small">
                        {c.what} · {formatBytes(c.size)}
                        {c.cloudName && " · in your cloud library"}
                      </span>
                    </span>
                    <label className="scan-col" data-tip={c.cloudName ? "Becomes the local copy of your cloud game" : undefined}>
                      <input type="checkbox" checked={chosen.has(c.key)} onChange={() => setChosen((set) => toggleIn(set, c.key))} />
                      <span className="scan-col-word">{c.cloudName ? "Link" : "Add"}</span>
                    </label>
                    {canUpload && (
                      <label className="scan-col" data-tip={c.cloudName ? "Already there" : !chosen.has(c.key) ? "Add it here first" : undefined}>
                        <input
                          type="checkbox"
                          checked={!!c.cloudName || uploads.has(c.key)}
                          disabled={!uploadable(c)}
                          onChange={() => setUploads((set) => toggleIn(set, c.key))}
                        />
                        <span className="scan-col-word">{c.cloudName ? "There" : "Upload"}</span>
                      </label>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              {progress && (
                <span className="muted small scan-progress">
                  <span className="spinner" aria-hidden="true" /> {progress}
                </span>
              )}
              <button type="button" className="btn" disabled={!!progress} onClick={() => setFound(null)}>
                Cancel
              </button>
              <button type="button" className="btn primary" disabled={!!progress || chosen.size === 0} onClick={add}>
                <MaterialIcon name="add" />
                {`Add ${chosen.size}`}
                {uploads.size > 0 && `, upload ${[...uploads].filter((k) => chosen.has(k)).length}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
